import { useEffect, useState } from "react";
import Layout, { PageHeader, Card } from "../components/Layout";
import { api } from "../lib/api";
import { Send, Loader2, Copy, Check, ExternalLink, RefreshCw, Trash2, ChevronDown, AlertCircle, Sparkles, Search, X, Linkedin, MailCheck } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  ["", "Select category"],
  ["footwear / sneaker brand", "Footwear / Sneaker"],
  ["apparel / streetwear brand", "Apparel / Streetwear"],
  ["F&B / beverage partner", "F&B / Beverage"],
  ["tech / consumer electronics", "Tech / Electronics"],
  ["BFSI / fintech", "BFSI / Fintech"],
  ["grooming / personal care", "Grooming / Care"],
  ["media / content partner", "Media / Content"],
  ["lifestyle / accessories", "Lifestyle / Accessories"],
  ["automobile", "Automobile"],
  ["other", "Other"],
];

const ASKS = [
  ["", "Select ask"],
  ["title or presenting sponsorship", "Title / Presenting Sponsor"],
  ["on-ground activation / pop-up booth", "Pop-up / Activation"],
  ["product seeding and gifting", "Product Seeding"],
  ["co-branded content collab", "Co-branded Content"],
  ["media partnership", "Media Partnership"],
  ["artist / stage sponsorship", "Artist / Stage Sponsor"],
  ["digital campaign collab", "Digital Campaign"],
  ["an exploratory call to discuss partnership", "Exploratory Call"],
];

const STATUSES = [
  { v: "drafted", label: "Drafted", cls: "text-zinc-300 border-zinc-700" },
  { v: "sent", label: "Sent", cls: "text-sky-300 border-sky-500/40" },
  { v: "replied", label: "Replied", cls: "text-amber-300 border-amber-500/40" },
  { v: "closed", label: "Closed", cls: "text-emerald-300 border-emerald-500/40" },
];

function gmailComposeUrl({ to, subject, body }) {
  const params = new URLSearchParams({
    view: "cm",
    fs: "1",
    to: to || "",
    su: subject || "",
    body: body || "",
  });
  return `https://mail.google.com/mail/?${params.toString()}`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const initialForm = {
  brand_name: "",
  ig_handle: "",
  brand_website: "",
  category: "",
  ask: "",
  contact_name: "",
  contact_email: "",
  additional_context: "",
};

export default function Outreach() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [selectedSubject, setSelectedSubject] = useState(0);
  const [copied, setCopied] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(true);

  // Apollo contact picker
  const [apolloOpen, setApolloOpen] = useState(false);

  const reloadHistory = async () => {
    try {
      const { data } = await api.get("/outreach/history?limit=50");
      setHistory(data.drafts || []);
    } catch {
      // silent
    }
  };
  useEffect(() => { reloadHistory(); }, []);

  // Loading step animation
  useEffect(() => {
    if (!loading) { setLoadingStep(0); return; }
    const steps = [0, 1, 2, 3];
    let i = 0;
    setLoadingStep(0);
    const t = setInterval(() => {
      i = Math.min(i + 1, steps.length - 1);
      setLoadingStep(i);
    }, 1400);
    return () => clearInterval(t);
  }, [loading]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const generate = async () => {
    setError("");
    if (!form.brand_name.trim()) {
      setError("Brand name is required.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/outreach/generate", {
        ...form,
        ig_handle: form.ig_handle.replace(/^@+/, ""),
      });
      setResult(data);
      setSelectedSubject(0);
      reloadHistory();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to generate";
      setError(typeof msg === "string" ? msg : "Failed to generate");
    } finally {
      setLoading(false);
    }
  };

  const regenerate = async () => {
    await generate();
  };

  const copyEmail = async () => {
    if (!result) return;
    const subject = result.subject_lines?.[selectedSubject] || "";
    const text = `Subject: ${subject}\n\n${result.email_body}`;
    let ok = false;
    // 1) Modern Clipboard API (works on https top-level)
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch { /* fall through */ }
    // 2) Fallback for iframe / insecure contexts — hidden textarea + execCommand
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.left = "0";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch { ok = false; }
    }
    if (ok) {
      setCopied(true);
      toast.success("Email copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error("Copy blocked — select the email body and press Ctrl/Cmd+C");
    }
  };

  const gmailUrl = result
    ? gmailComposeUrl({
        to: result.contact_email,
        subject: result.subject_lines?.[selectedSubject] || "",
        body: result.email_body,
      })
    : "#";

  const setStatus = async (draftId, status) => {
    try {
      await api.patch(`/outreach/${draftId}`, { status });
      reloadHistory();
      if (result?.id === draftId) setResult((r) => ({ ...r, status }));
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Status update failed");
    }
  };

  const deleteDraft = async (draftId) => {
    if (!window.confirm("Delete this draft? This cannot be undone.")) return;
    try {
      await api.delete(`/outreach/${draftId}`);
      reloadHistory();
      if (result?.id === draftId) setResult(null);
      toast.success("Draft deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const loadDraft = (d) => {
    setResult(d);
    setSelectedSubject(0);
    setForm({
      brand_name: d.brand_name || "",
      ig_handle: d.ig_handle || "",
      brand_website: d.brand_website || "",
      category: d.category || "",
      ask: d.ask || "",
      contact_name: d.contact_name || "",
      contact_email: d.contact_email || "",
      additional_context: d.additional_context || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const loadingSteps = [
    "Analyzing brand positioning",
    "Finding personalization hooks",
    "Drafting founder email",
    "Finalizing subject lines",
  ];

  return (
    <Layout>
      <PageHeader
        overline="// Founder Outreach System"
        title="Outreach Studio"
        subtitle="Enter a brand's details. Get a researched, personalized email — in your voice, ready to send."
        testId="outreach-header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: form */}
        <Card className="p-6" testId="outreach-form">
          <div className="overline mb-5 text-zinc-500">// Brand Details</div>

          <Field label="Brand Name *">
            <input
              type="text"
              value={form.brand_name}
              onChange={setField("brand_name")}
              placeholder="e.g. Puma India, Snitch, Noise"
              data-testid="outreach-brand"
              className={inputCls}
            />
          </Field>

          <Field label="Instagram Handle">
            <div className="flex items-stretch">
              <span className="px-3 grid place-items-center bg-[#0f0f0f] border border-r-0 border-zinc-800 text-zinc-500 text-sm">@</span>
              <input
                type="text"
                value={form.ig_handle}
                onChange={setField("ig_handle")}
                placeholder="pumaindia"
                data-testid="outreach-ig"
                className={`${inputCls} flex-1`}
              />
            </div>
          </Field>

          <Field label="Brand Website" optional>
            <input
              type="text"
              value={form.brand_website}
              onChange={setField("brand_website")}
              placeholder="pumaindia.com"
              data-testid="outreach-website"
              className={inputCls}
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Partnership Category">
              <select
                value={form.category}
                onChange={setField("category")}
                data-testid="outreach-category"
                className={`${inputCls} appearance-none cursor-pointer`}
              >
                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Specific Ask">
              <select
                value={form.ask}
                onChange={setField("ask")}
                data-testid="outreach-ask"
                className={`${inputCls} appearance-none cursor-pointer`}
              >
                {ASKS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          </div>

          <div className="flex items-end justify-between mb-1">
            <label className="block overline text-zinc-500">
              Contact Name
              <span className="ml-1.5 normal-case tracking-normal text-[10px] text-zinc-700">(if you know who to reach)</span>
            </label>
            <button
              type="button"
              onClick={() => setApolloOpen(true)}
              disabled={!form.brand_name.trim() && !form.brand_website.trim()}
              data-testid="outreach-find-apollo"
              className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-md"
              title={form.brand_name || form.brand_website ? "" : "Enter brand name or website first"}
            >
              <Search className="h-3 w-3" />
              Find via Apollo
            </button>
          </div>
          <input
            type="text"
            value={form.contact_name}
            onChange={setField("contact_name")}
            placeholder="e.g. Rohit Sharma, Marketing Head"
            data-testid="outreach-contact-name"
            className={`${inputCls} mb-4`}
          />

          <Field label="Contact Email" optional>
            <input
              type="text"
              value={form.contact_email}
              onChange={setField("contact_email")}
              placeholder="rohit@brand.com"
              data-testid="outreach-contact-email"
              className={inputCls}
            />
          </Field>

          <Field label="Additional Context" optional>
            <textarea
              value={form.additional_context}
              onChange={setField("additional_context")}
              placeholder="Mutual connection, recent news about the brand, specific angle..."
              data-testid="outreach-context"
              rows={3}
              className={`${inputCls} resize-none`}
            />
          </Field>

          <button
            onClick={generate}
            disabled={loading || !form.brand_name.trim()}
            data-testid="outreach-generate"
            className="w-full mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 bg-emerald-500 hover:bg-emerald-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-black font-medium text-sm transition-colors"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Generate Email
              </>
            )}
          </button>
        </Card>

        {/* RIGHT: output */}
        <Card className="p-6 flex flex-col min-h-[600px]" testId="outreach-output">
          <div className="overline mb-5 text-zinc-500">// Email Output</div>

          {!loading && !result && !error && (
            <div className="flex-1 grid place-items-center text-center px-6">
              <div>
                <div className="h-10 w-10 mx-auto mb-4 border border-zinc-800 grid place-items-center text-zinc-700">
                  <Send className="h-4 w-4" />
                </div>
                <p className="text-sm text-zinc-500 max-w-xs">
                  Fill in the brand details and hit Generate. Drafts are auto-saved to your history below.
                </p>
              </div>
            </div>
          )}

          {loading && (
            <div className="flex-1 grid place-items-center">
              <div className="text-center">
                <div className="h-10 w-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-5"></div>
                <div className="text-sm font-medium mb-4">{loadingSteps[loadingStep]}</div>
                <div className="space-y-2 text-left max-w-xs mx-auto">
                  {loadingSteps.map((s, i) => (
                    <div key={i} className={`flex items-center gap-2 text-xs ${i <= loadingStep ? "text-zinc-300" : "text-zinc-600"}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${i < loadingStep ? "bg-emerald-500" : i === loadingStep ? "bg-emerald-500 animate-pulse" : "bg-zinc-700"}`} />
                      {s}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="p-4 border border-rose-500/30 bg-rose-500/5 text-sm text-rose-200 flex items-start gap-2" data-testid="outreach-error">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>{error}</div>
            </div>
          )}

          {result && !loading && (
            <div className="space-y-5 flex-1" data-testid="outreach-result">
              {/* Contact */}
              {(result.contact_name || result.contact_email) && (
                <div className="pb-4 border-b border-zinc-900">
                  <div className="overline mb-1.5 text-zinc-500">Contact</div>
                  <div className="text-sm text-zinc-200">
                    {result.contact_name || "—"}
                    {result.contact_email && (
                      <span className="text-zinc-500 ml-2 text-xs">· {result.contact_email}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Research Brief */}
              <div>
                <div className="overline mb-2 text-zinc-500">Research Brief</div>
                <div className="text-[13px] leading-relaxed text-zinc-300 whitespace-pre-wrap">
                  {result.research_brief}
                </div>
                {result.research_pills?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {result.research_pills.map((p, i) => (
                      <span key={i} className="text-[11px] px-2 py-1 bg-[#0f0f0f] border border-zinc-800 text-zinc-300">
                        {p}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Subjects */}
              <div>
                <div className="overline mb-2 text-zinc-500">Pick a Subject Line</div>
                <div className="space-y-1.5">
                  {result.subject_lines?.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedSubject(i)}
                      data-testid={`outreach-subject-${i}`}
                      className={`w-full text-left px-3 py-2.5 border text-sm transition-colors ${
                        selectedSubject === i
                          ? "border-emerald-500/60 bg-emerald-500/5 text-white"
                          : "border-zinc-800 text-zinc-300 hover:border-zinc-700 hover:bg-[#0f0f0f]"
                      }`}
                    >
                      <span className="text-zinc-600 text-[11px] mr-2 tabular">0{i + 1}</span>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Body */}
              <div>
                <div className="overline mb-2 text-zinc-500">Email Body</div>
                <div
                  className="p-4 border border-zinc-800 bg-[#0a0a0a] text-[13px] leading-relaxed text-zinc-200 whitespace-pre-wrap font-mono select-text cursor-text"
                  onClick={(e) => {
                    // triple-click anywhere selects the whole body
                    const range = document.createRange();
                    range.selectNodeContents(e.currentTarget);
                    const sel = window.getSelection();
                    sel.removeAllRanges();
                    sel.addRange(range);
                  }}
                  title="Click to select all · then Ctrl/Cmd+C"
                  data-testid="outreach-email-body"
                >
                  {result.email_body}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  onClick={copyEmail}
                  data-testid="outreach-copy"
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-800 text-zinc-200 hover:bg-[#181818] text-xs transition-colors"
                >
                  {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                  {copied ? "Copied" : "Copy"}
                </button>
                <a
                  href={gmailUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="outreach-open-gmail"
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-800 text-zinc-200 hover:bg-[#181818] text-xs transition-colors"
                >
                  <ExternalLink className="h-3 w-3" />
                  Open Gmail
                </a>
                {result.id && result.status !== "sent" && (
                  <button
                    onClick={() => setStatus(result.id, "sent")}
                    data-testid="outreach-mark-sent"
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-xs transition-colors"
                  >
                    <Send className="h-3 w-3" />
                    Mark sent
                  </button>
                )}
                <button
                  onClick={regenerate}
                  data-testid="outreach-regenerate"
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-medium transition-colors ml-auto"
                >
                  <RefreshCw className="h-3 w-3" />
                  Regenerate
                </button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* HISTORY */}
      <section className="mt-10">
        <button
          onClick={() => setHistoryOpen((x) => !x)}
          className="w-full flex items-center justify-between mb-3 text-left"
        >
          <div className="flex items-baseline gap-3">
            <h3 className="font-display text-lg">Outreach History</h3>
            <span className="overline text-zinc-600">{history.length} draft{history.length === 1 ? "" : "s"}</span>
          </div>
          <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${historyOpen ? "rotate-180" : ""}`} />
        </button>

        {historyOpen && (
          <Card className="overflow-hidden" testId="outreach-history">
            {history.length === 0 ? (
              <div className="p-8 text-center text-sm text-zinc-500">No drafts yet — generate your first one above.</div>
            ) : (
              <div className="divide-y divide-zinc-900">
                {history.map((d) => {
                  const status = STATUSES.find((s) => s.v === d.status) || STATUSES[0];
                  return (
                    <div
                      key={d.id}
                      className="p-4 hover:bg-[#161616] transition-colors flex items-start gap-4"
                      data-testid={`outreach-history-${d.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <div className="font-medium text-sm">{d.brand_name}</div>
                          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${status.cls}`}>
                            {status.label}
                          </span>
                          {d.category && (
                            <span className="text-[11px] text-zinc-500">· {d.category}</span>
                          )}
                          <span className="text-[11px] text-zinc-600 ml-auto">{timeAgo(d.created_at)}</span>
                        </div>
                        <div className="text-xs text-zinc-400 truncate">
                          {d.subject_lines?.[0] || d.research_brief}
                        </div>
                        {d.contact_email && (
                          <div className="text-[11px] text-zinc-600 mt-1">→ {d.contact_email}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          onClick={() => loadDraft(d)}
                          className="text-[11px] px-2 py-1 border border-zinc-800 text-zinc-300 hover:bg-[#181818]"
                          data-testid={`outreach-load-${d.id}`}
                        >
                          Open
                        </button>
                        <select
                          value={d.status || "drafted"}
                          onChange={(e) => setStatus(d.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="text-[11px] px-1.5 py-1 border border-zinc-800 bg-[#0f0f0f] text-zinc-300 cursor-pointer"
                          data-testid={`outreach-status-${d.id}`}
                        >
                          {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                        </select>
                        <button
                          onClick={() => deleteDraft(d.id)}
                          className="p-1.5 text-zinc-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                          data-testid={`outreach-delete-${d.id}`}
                          aria-label="Delete draft"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        )}
      </section>

      {apolloOpen && (
        <ApolloPicker
          brandName={form.brand_name}
          brandWebsite={form.brand_website}
          onClose={() => setApolloOpen(false)}
          onPick={(c) => {
            setForm((f) => ({
              ...f,
              contact_name: c.name || f.contact_name,
              contact_email: c.email || f.contact_email,
            }));
            setApolloOpen(false);
            toast.success(`Pulled ${c.name} from Apollo`);
          }}
        />
      )}
    </Layout>
  );
}

/* ============================================================ */

const DEFAULT_TITLES = [
  "CMO", "Chief Marketing Officer", "Head of Marketing", "Marketing Director",
  "VP Marketing", "Brand Manager", "Brand Director",
  "Marketing Manager", "Growth Lead", "Head of Partnerships", "Partnerships Manager",
  "Sponsorships Manager", "Head of Brand",
];

function ApolloPicker({ brandName, brandWebsite, onClose, onPick }) {
  const [titles, setTitles] = useState(DEFAULT_TITLES.slice(0, 7));
  const [domain, setDomain] = useState(brandWebsite || "");
  const [name, setName] = useState(brandName || "");
  const [people, setPeople] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [enrichingId, setEnrichingId] = useState(null);
  const [err, setErr] = useState("");

  const toggleTitle = (t) => {
    setTitles((arr) => arr.includes(t) ? arr.filter((x) => x !== t) : [...arr, t]);
  };

  const search = async (p = 1) => {
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/apollo/search", {
        company_domain: domain.trim(),
        company_name: name.trim(),
        titles,
        page: p,
        per_page: 10,
      });
      setPeople(data.people || []);
      setTotal(data.total || 0);
      setPage(p);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Search failed";
      setErr(typeof msg === "string" ? msg : "Search failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { search(1); }, []);

  const enrichAndPick = async (apolloId) => {
    setEnrichingId(apolloId);
    try {
      const { data } = await api.post("/apollo/enrich", { apollo_id: apolloId, reveal_personal_emails: false });
      const c = data.contact;
      if (!c.email) {
        toast.error("No verified email found for this contact");
        setEnrichingId(null);
        return;
      }
      onPick(c);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Enrichment failed";
      toast.error(typeof msg === "string" ? msg : "Enrichment failed");
      setEnrichingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="apollo-picker">
      <button onClick={onClose} className="flex-1 bg-black/70 backdrop-blur-sm" aria-label="Close" />
      <aside className="w-full max-w-[640px] bg-[#0a0f1e] border-l border-white/5 overflow-y-auto flex flex-col">
        <header className="px-6 py-5 border-b border-white/5 sticky top-0 bg-[#0a0f1e] z-10 flex items-center justify-between">
          <div>
            <div className="overline text-zinc-500 mb-1">Apollo · Contact Finder</div>
            <div className="font-display font-bold text-xl">Find the right person</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/[0.04] rounded-md" data-testid="apollo-close">
            <X className="h-5 w-5 text-zinc-400" />
          </button>
        </header>

        <div className="p-6 space-y-4 border-b border-white/5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block overline text-zinc-500 mb-1.5">Domain</label>
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="puma.com"
                data-testid="apollo-domain"
                className="w-full px-3 py-2 bg-[#070b16] border border-white/10 text-sm text-zinc-100 focus:border-sky-500 focus:outline-none rounded-md"
              />
            </div>
            <div>
              <label className="block overline text-zinc-500 mb-1.5">Brand name <span className="normal-case tracking-normal text-[10px] text-zinc-700">(fallback)</span></label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Puma India"
                data-testid="apollo-name"
                className="w-full px-3 py-2 bg-[#070b16] border border-white/10 text-sm text-zinc-100 focus:border-sky-500 focus:outline-none rounded-md"
              />
            </div>
          </div>
          <div>
            <label className="block overline text-zinc-500 mb-1.5">Titles ({titles.length} selected)</label>
            <div className="flex flex-wrap gap-1.5">
              {DEFAULT_TITLES.map((t) => {
                const on = titles.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTitle(t)}
                    data-testid={`apollo-title-${t}`}
                    className={`text-[11px] px-2 py-1 border rounded-md transition-colors ${
                      on
                        ? "border-sky-500/60 bg-sky-500/10 text-sky-200"
                        : "border-white/10 text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>
          <button
            onClick={() => search(1)}
            disabled={loading || titles.length === 0 || (!domain.trim() && !name.trim())}
            data-testid="apollo-search-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-sky-500 hover:bg-sky-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-black text-sm font-medium transition-colors rounded-md"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {loading ? "Searching…" : "Search Apollo"}
          </button>
        </div>

        {err && (
          <div className="m-6 p-3 border border-rose-500/30 bg-rose-500/5 text-sm text-rose-200 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5" /> {err}
          </div>
        )}

        <div className="flex-1 px-6 py-4">
          {!loading && people.length === 0 && !err && (
            <div className="text-center py-10 text-sm text-zinc-500">No results yet — adjust titles or domain and search.</div>
          )}
          {people.length > 0 && (
            <>
              <div className="text-xs text-zinc-500 mb-3 flex items-center justify-between">
                <span>Showing <span className="text-zinc-300">{people.length}</span> of {total.toLocaleString("en-IN")} potential matches</span>
                <span className="text-[10px] text-zinc-600">Click a row to reveal email (1 Apollo credit)</span>
              </div>
              <div className="space-y-1.5">
                {people.map((p) => (
                  <button
                    key={p.apollo_id}
                    onClick={() => enrichAndPick(p.apollo_id)}
                    disabled={enrichingId !== null}
                    data-testid={`apollo-result-${p.apollo_id}`}
                    className="w-full text-left p-3 border border-white/5 hover:border-sky-500/40 hover:bg-sky-500/5 disabled:opacity-50 transition-colors rounded-lg group"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm text-zinc-100 truncate">
                          {p.first_name} {p.last_name_masked}
                        </div>
                        <div className="text-xs text-zinc-400 truncate mt-0.5">
                          {p.title} · <span className="text-zinc-500">{p.organization_name}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-[10px]">
                          {p.has_email && <span className="inline-flex items-center gap-1 text-emerald-400"><MailCheck className="h-2.5 w-2.5" /> Email available</span>}
                          {p.has_phone && <span className="text-zinc-500">· Phone</span>}
                        </div>
                      </div>
                      <div className="shrink-0">
                        {enrichingId === p.apollo_id ? (
                          <Loader2 className="h-4 w-4 animate-spin text-sky-400" />
                        ) : (
                          <span className="text-[10px] uppercase tracking-wider text-sky-400 opacity-0 group-hover:opacity-100 transition-opacity">Reveal →</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between mt-4 text-xs">
                <button
                  onClick={() => search(Math.max(1, page - 1))}
                  disabled={page <= 1 || loading}
                  className="px-3 py-1.5 border border-white/10 text-zinc-300 disabled:opacity-30 hover:bg-white/[0.04] rounded-md"
                >
                  ← Prev
                </button>
                <span className="text-zinc-500 tabular">Page {page}</span>
                <button
                  onClick={() => search(page + 1)}
                  disabled={people.length < 10 || loading}
                  className="px-3 py-1.5 border border-white/10 text-zinc-300 disabled:opacity-30 hover:bg-white/[0.04] rounded-md"
                >
                  Next →
                </button>
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2.5 bg-[#0a0a0a] border border-zinc-800 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-emerald-500 focus:outline-none transition-colors";

function Field({ label, optional, children }) {
  return (
    <div className="mb-4">
      <label className="block overline text-zinc-500 mb-1.5">
        {label}
        {optional && (
          <span className="ml-1.5 normal-case tracking-normal text-[10px] text-zinc-700">
            ({typeof optional === "string" ? optional : "optional"})
          </span>
        )}
      </label>
      {children}
    </div>
  );
}
